import { useCallback } from "react";
import BackgroundProgressiveImg from "app/pages/.shared/BackgroundProgressiveImg.jsx";
import { ficheProduitPropTypes } from "app/utils/propTypes";
import "./Jumbotron.scss";
import {
	FLAGSHIP_EVENT_KPIS,
	OFFER_CONTRACT_TYPES,
	PRODUCT_EXPERIENCES_WITH_HOTEL,
	PRODUCT_TAG_CODES,
} from "app/constants";
import ProductDestinationTitleContainer from "app/pages/.shared/ProductDestinationTitle/ProductDestinationTitleContainer";
import ProductNameWithCategory from "app/pages/.shared/ProductNameWithCategory/ProductNameWithCategory";
import ProductLinkContainer from "app/pages/.shared/ProductLink/ProductLinkContainer";
import ProductDeal from "app/pages/.shared/Deal/ProductDeal";
import { EventCategory, HitType, useFlagship } from "@flagship.io/react-sdk";
import { useLocation } from "react-router-dom";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { getUnbeatableOffer } from "app/utils/productUtils";
import { shouldShowFavoriteBadge } from "app/utils/utils";
import ProductBadge from "app/features/product/components/ProductBadge/ProductBadge";
import { mapFicheProduitStoreToTag } from "app/utils/analytics-store-mapper";
import { ArrowRightIcon } from "app/icons/navigation";

const IMG_SIZES = {
	small: { width: 800, height: 460 },
	medium: { width: 1024, height: 500 },
	large: { height: 460 },
	xlarge: { height: 460 },
};

const CLOUDINARY_OPTIONS = ["c_fill"];

const Jumbotron = ({ product = {} }) => {
	const { pathname } = useLocation();
	const { sendHits } = useFlagship();

	const { track } = useAnalytics();

	const unbeatableOfferTag = getUnbeatableOffer(product);
	const isFavorite = shouldShowFavoriteBadge(product);

	const allTags = [
		...(unbeatableOfferTag ? [unbeatableOfferTag] : []),
		...(isFavorite ? [PRODUCT_TAG_CODES.COUP_DE_COEUR] : []),
	];

	const handleProductClick = useCallback(() => {
		sendHits({
			type: HitType.EVENT,
			category: EventCategory.ACTION_TRACKING,
			action: FLAGSHIP_EVENT_KPIS.CLICK_ON_PP_PRODUCT_CARD,
			label: pathname,
		});

		track("view_offer", {
			position: 0,
			tags: allTags,
			product: mapFicheProduitStoreToTag(product),
			startingPrice: product?.fromPriceType?.value ?? 0,
		});
	}, [pathname]);

	const mainPhoto = product.photos && product.photos[0];

	return (
		<div className="product product--spotlighted" data-testid="product">
			<div className="product__picture">
				<ProductLinkContainer
					handleProductClick={handleProductClick}
					productUri={product.uri}
				>
					<BackgroundProgressiveImg
						sizes={IMG_SIZES}
						cloudinaryOptions={CLOUDINARY_OPTIONS}
						src={mainPhoto?.url}
						thumbnail={mainPhoto?.thumbnail}
						quality="q_auto:best"
					/>
				</ProductLinkContainer>
			</div>
			<div className="product__deal">
				<ProductDeal
					isRental={product.contractType === OFFER_CONTRACT_TYPES.RENTAL}
					publicPrice={product.fromPriceType?.publicPrice}
					price={product.fromPriceType?.price}
					includeOfferWithFlight={product.includeOfferWithFlight}
					duration={product.fromPriceType?.numberOfNights}
					hour={product.expiration?.hour}
					day={product.expiration?.day}
					type={product.fromPriceType?.type}
					value={product.fromPriceType?.value}
					isWithHotel={PRODUCT_EXPERIENCES_WITH_HOTEL.includes(
						product?.productExperience
					)}
				/>
			</div>
			<div className="product__footer">
				<div className="product__main">
					<ProductDestinationTitleContainer
						className="product__destination"
						region={product.region}
						resort={product.resort}
						country={product.country}
					/>

					<ProductNameWithCategory
						categoryType={product.categoryType}
						category={product.category}
						name={product.name}
					/>

					{product.badges && product.badges.length > 0 && (
						<div className="product__badges">
							{product.badges.map((badge, id) => {
								return <ProductBadge key={id} label={badge.label} />;
							})}
						</div>
					)}
				</div>
				<div className="product__deal">
					<ProductDeal
						isRental={product.contractType === OFFER_CONTRACT_TYPES.RENTAL}
						publicPrice={product.fromPriceType?.publicPrice}
						price={product.fromPriceType?.price}
						includeOfferWithFlight={product.includeOfferWithFlight}
						duration={product.fromPriceType?.numberOfNights}
						hour={product.expiration?.hour}
						day={product.expiration?.day}
						type={product.fromPriceType?.type}
						value={product.fromPriceType?.value}
						isWithHotel={PRODUCT_EXPERIENCES_WITH_HOTEL.includes(
							product?.productExperience
						)}
					/>
				</div>
				<div className="product__action">
					<ProductLinkContainer
						handleProductClick={handleProductClick}
						productUri={product.uri}
					>
						<ArrowRightIcon width={20} height={20} />
					</ProductLinkContainer>
				</div>
			</div>
		</div>
	);
};

Jumbotron.propTypes = {
	product: ficheProduitPropTypes,
};

export default Jumbotron;
